import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = () => {
      const storedToken = localStorage.getItem('token');
      const storedUserInfo = localStorage.getItem('userInfo');

      if (storedToken && storedUserInfo) {
        try {
          setUser(JSON.parse(storedUserInfo));
          // Los datos guardados pueden ser de hace semanas: si le cambiaron el rol,
          // el area o los permisos, sin esto seguiria con los viejos hasta que
          // cerrara sesion. Se refresca en segundo plano, sin bloquear el arranque.
          refrescarUsuario(storedToken);
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          setUser(null);
        }
      }
      setLoading(false);
    };

    const refrescarUsuario = async (token) => {
      try {
        const res = await fetch(`${API_URL}/api/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return; // token vencido o backend caido: lo maneja el resto de la app
        const data = await res.json();
        if (data?.user) {
          localStorage.setItem('userInfo', JSON.stringify(data.user));
          setUser(data.user);
        }
      } catch (error) {
        // Sin conexion se sigue con lo guardado; no vale la pena sacar al usuario.
      }
    };

    initializeAuth();
  }, []); // Se ejecuta solo una vez al montar el componente

  const login = async (username, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error al iniciar sesión');
    }

    const data = await response.json();

    localStorage.setItem('token', data.token);
    localStorage.setItem('userInfo', JSON.stringify(data.user));
    setUser(data.user);

    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    setUser(null);
  };

  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    if (token) {
      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      };
    }

    const response = await fetch(url, options);

    if (response.status === 401) {
      logout();
      // Forzar recarga para redirigir a login
      window.location.href = '/login'; 
      throw new Error('Sesión expirada');
    }

    return response;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    authFetch
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}; 